/**
 * DiagnosticAttachments — reusable picker for diagnostic submissions (#1965).
 *
 * Lets the rep attach up to MAX_PHOTOS iPhone screenshots and a freeform
 * notes block to any diagnostic submission. Compresses each image client-
 * side (canvas downscale + JPG re-encode) so the inline-base64 payload
 * stays small (~100-250 KB per shot vs raw 3-5 MB iPhone PNGs).
 *
 * Used by SendDiagnostics, GpsDiagnostics, DiagnosticsScanner. The
 * compressed images + notes get embedded under
 * `payload.context.attachments` and surfaced on the admin detail view.
 *
 * Controlled component: parent owns the value and re-renders this with
 * a fresh prop each change. Parent submits when the user taps Send.
 */
import { useState } from 'react';

export interface DiagnosticAttachment {
  filename: string;
  mimeType: 'image/jpeg';
  sizeBytes: number;
  /** Base64-encoded JPG bytes WITHOUT the data: prefix. */
  base64: string;
}

export interface DiagnosticAttachmentsValue {
  notes: string;
  screenshots: DiagnosticAttachment[];
}

interface DiagnosticAttachmentsProps {
  value: DiagnosticAttachmentsValue;
  onChange: (next: DiagnosticAttachmentsValue) => void;
  /** Optional override of the notes-area label so each diagnostic
   *  page can frame the prompt appropriately. */
  notesLabel?: string;
  /** Hint text shown under the notes textarea. */
  notesHint?: string;
  /** Disable the picker + textarea while a submit is in flight. */
  disabled?: boolean;
}

const MAX_PHOTOS = 6;
const MAX_LONG_EDGE_PX = 1200;
const JPEG_QUALITY = 0.7;
// Hard size guard per photo so a wildly uncompressible image (rare for
// screenshots) doesn't blow up the payload. Anything still over after
// re-encoding is flagged with a warning rather than silently shipped.
const MAX_BYTES_PER_PHOTO = 600_000;

export default function DiagnosticAttachments({
  value,
  onChange,
  notesLabel = 'Notes & Details',
  notesHint = "Anything that didn't behave right? What were you doing when it happened? What did you expect?",
  disabled = false,
}: DiagnosticAttachmentsProps) {
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setWarning(null);
    setBusy(true);

    try {
      const remaining = MAX_PHOTOS - value.screenshots.length;
      if (remaining <= 0) {
        setWarning(`Already at the ${MAX_PHOTOS}-photo limit. Remove one to add another.`);
        return;
      }

      const incoming = Array.from(files).slice(0, remaining);
      if (files.length > incoming.length) {
        setWarning(
          `Only the first ${incoming.length} can be added — limit is ${MAX_PHOTOS} total.`,
        );
      }

      const processed: DiagnosticAttachment[] = [];
      const oversized: string[] = [];
      for (const file of incoming) {
        if (!file.type.startsWith('image/')) continue;
        try {
          const attachment = await compressImage(file);
          if (attachment.sizeBytes > MAX_BYTES_PER_PHOTO) {
            oversized.push(`${file.name} (${(attachment.sizeBytes / 1024).toFixed(0)} KB)`);
          }
          processed.push(attachment);
        } catch (err) {
          console.warn('[DiagnosticAttachments] compress failed for', file.name, err);
          oversized.push(`${file.name} (compression failed)`);
        }
      }

      if (oversized.length) {
        setWarning(
          (prev) =>
            (prev ? prev + ' ' : '')
            + `Heads up — these came in larger than expected even after compression: ${oversized.join(', ')}.`,
        );
      }

      onChange({ ...value, screenshots: [...value.screenshots, ...processed] });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = (idx: number) => {
    const next = value.screenshots.slice();
    next.splice(idx, 1);
    onChange({ ...value, screenshots: next });
  };

  const totalKb = value.screenshots.reduce((s, a) => s + a.sizeBytes, 0) / 1024;

  return (
    <div className="rounded-xl bg-white p-4 shadow-card space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="diag-notes">
          {notesLabel}
        </label>
        <textarea
          id="diag-notes"
          value={value.notes}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
          disabled={disabled}
          rows={5}
          className="w-full rounded-lg border border-slate-300 p-2 text-sm leading-relaxed focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60"
          placeholder={notesHint}
        />
        <p className="mt-1 text-xs text-slate-500">{value.notes.length.toLocaleString()} characters</p>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="block text-sm font-medium text-slate-700" htmlFor="diag-screenshots">
            Screenshots
          </label>
          <span className="text-xs text-slate-500">
            {value.screenshots.length} / {MAX_PHOTOS}
            {value.screenshots.length > 0 && (
              <> · {totalKb.toFixed(0)} KB total</>
            )}
          </span>
        </div>

        {/* Picker. accept="image/*" without `capture` opens the iOS Photos
            library picker (the catch-all entry for screenshots) rather
            than the camera. multiple lets the rep pick a batch in one
            tap. */}
        <label
          htmlFor="diag-screenshots"
          className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed py-3 text-sm font-medium transition-colors ${
            disabled || busy || value.screenshots.length >= MAX_PHOTOS
              ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
              : 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
          }`}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {busy
            ? 'Compressing…'
            : value.screenshots.length >= MAX_PHOTOS
              ? 'Photo limit reached'
              : 'Add screenshots from Photos'}
        </label>
        <input
          id="diag-screenshots"
          type="file"
          accept="image/*"
          multiple
          disabled={disabled || busy || value.screenshots.length >= MAX_PHOTOS}
          onChange={(e) => {
            void handleFiles(e.target.files);
            // Reset so picking the same file again is a no-op vs ignored.
            e.target.value = '';
          }}
          className="sr-only"
        />

        {warning && (
          <p className="mt-2 rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
            {warning}
          </p>
        )}

        {value.screenshots.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {value.screenshots.map((sc, idx) => (
              <div key={idx} className="relative">
                <img
                  src={`data:${sc.mimeType};base64,${sc.base64}`}
                  alt={sc.filename}
                  className="aspect-square w-full rounded-lg object-cover border border-slate-200"
                />
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  disabled={disabled}
                  aria-label={`Remove ${sc.filename}`}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <p className="mt-1 text-[10px] text-slate-500 truncate">
                  {(sc.sizeBytes / 1024).toFixed(0)} KB
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

async function compressImage(file: File): Promise<DiagnosticAttachment> {
  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);
  const { width, height } = scaleDimensions(img.naturalWidth, img.naturalHeight, MAX_LONG_EDGE_PX);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  const base64 = await readAsBase64(blob);
  const originalName = file.name || 'screenshot';
  return {
    filename: rewriteExtensionToJpg(originalName),
    mimeType: 'image/jpeg',
    sizeBytes: blob.size,
    base64,
  };
}

function readAsDataURL(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function readAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = typeof reader.result === 'string' ? reader.result : '';
      // strip "data:image/jpeg;base64," prefix
      const idx = r.indexOf(',');
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('canvas.toBlob returned null'));
      },
      type,
      quality,
    );
  });
}

function scaleDimensions(w: number, h: number, maxLongEdge: number): { width: number; height: number } {
  if (w <= maxLongEdge && h <= maxLongEdge) return { width: w, height: h };
  const ratio = w > h ? maxLongEdge / w : maxLongEdge / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function rewriteExtensionToJpg(name: string): string {
  // iOS screenshots arrive as IMG_xxxx.PNG. Re-encoding to JPG means
  // the extension should reflect that — keeps the admin UI honest.
  return name.replace(/\.(png|heic|heif|webp|gif|bmp)$/i, '.jpg');
}
